/**
 * Error handling utilities for API and form validation errors
 */
import toast from "react-hot-toast";
import type { ApiError } from "./types";

export interface ProblemDetail {
  type: string;
  title: string;
  status: number;
  detail?: string;
  instance?: string;
  errors?: Array<{ pointer: string; message: string }>;
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

/**
 * Checks if an object is a ProblemDetail according to RFC 7807
 */
function isProblemDetailObject(
  obj: unknown,
): obj is ProblemDetail {
  if (!isObject(obj)) return false;

  return (
    typeof obj.type === "string" &&
    typeof obj.title === "string" &&
    typeof obj.status === "number"
  );
}

/**
 * Checks if error is wrapped in openapi-react-query format with ProblemDetail
 */
function isProblemDetail(
  error: unknown,
): error is { response: { data: ProblemDetail; status: number } } {
  if (!isObject(error)) return false;
  if (!isObject(error.response)) return false;

  const data = error.response.data;
  return isProblemDetailObject(data);
}

function isApiErrorResponse(
  error: unknown,
): error is { response: { data: { error: string }; status: number } } {
  if (!isObject(error)) return false;
  if (!isObject(error.response)) return false;

  const data = error.response.data;
  if (!isObject(data)) return false;

  return typeof data.error === "string";
}

function isFetchError(
  error: unknown,
): error is { error: string | { message: string } } {
  if (!isObject(error)) return false;

  if (typeof error.error === "string") return true;
  if (isObject(error.error) && typeof error.error.message === "string")
    return true;

  return false;
}

function isMessageError(error: unknown): error is { message: string } {
  if (!isObject(error)) return false;
  return typeof error.message === "string";
}

function isZodError(
  error: unknown,
): error is { issues: Array<{ message: string }> } {
  if (!isObject(error)) return false;
  if (!Array.isArray(error.issues)) return false;
  if (error.issues.length === 0) return false;

  const firstIssue = error.issues[0];
  if (!isObject(firstIssue)) return false;

  return typeof firstIssue.message === "string";
}

/**
 * Gets a default user-friendly message for HTTP status codes
 * based on RFC 7231 and common practices
 */
function getDefaultStatusMessage(status: number): string {
  const statusMessages: Record<number, string> = {
    400: "The request was invalid. Please check your input and try again.",
    401: "You are not authorized to perform this action. Please sign in.",
    403: "You don't have permission to access this resource.",
    404: "The requested resource was not found.",
    409: "This action conflicts with existing data. Please check and try again.",
    422: "The request was well-formed but contains semantic errors.",
    429: "Too many requests. Please wait a moment and try again.",
    500: "An internal server error occurred. Please try again later.",
    502: "The server is temporarily unavailable. Please try again later.",
    503: "The service is temporarily unavailable. Please try again later.",
    504: "The request timed out. Please try again.",
  };

  return (
    statusMessages[status] ||
    `An error occurred (${status}). Please try again.`
  );
}

/**
 * Extracts and formats a message from a ProblemDetail object
 * following RFC 7807 Problem Details specification
 */
function extractProblemDetailMessage(
  problem: ProblemDetail,
): string {
  // Priority 1: Use detail if it exists and is not empty
  if (problem.detail && problem.detail.trim()) {
    // If there are validation errors and the detail is generic, prefer the first error
    if (
      problem.errors &&
      problem.errors.length > 0 &&
      (problem.detail === "Validation failed" ||
        problem.detail === "Bad Request" ||
        problem.title === problem.detail)
    ) {
      return problem.errors[0].message;
    }
    return problem.detail;
  }

  // Priority 2: Use validation errors if available
  if (problem.errors && problem.errors.length > 0) {
    return problem.errors[0].message;
  }

  // Priority 3: Use title if it's not generic
  const genericTitles = ["Bad Request", "Internal Server Error", "Error"];
  if (problem.title && !genericTitles.includes(problem.title)) {
    return problem.title;
  }

  // Priority 4: Use status-based default message
  return getDefaultStatusMessage(problem.status);
}

/**
 * Handles various error formats and converts them to a standardized ApiError format.
 * Supports:
 * - RFC 7807 Problem Details: { response: { data: ProblemDetail, status: number } }
 * - ProblemDetail objects directly
 * - openapi-react-query error format: { response: { data: { error: string }, status: number } }
 * - Fetch error format: { error: { message: string } } or { error: string }
 * - Error objects with message property: { message: string }
 * - Zod validation errors: { issues: Array<{ path: string[], message: string }> }
 * - Plain string errors
 *
 * @param error - The error to handle (can be any type)
 * @returns A standardized ApiError object with a message and optional status/code
 */
export const handleApiError = (error: unknown): ApiError => {
  // Extract status code from error.response.status (openapi-react-query format)
  let status: number | undefined;
  if (
    isObject(error) &&
    isObject(error.response) &&
    typeof error.response.status === "number"
  ) {
    status = error.response.status;
  }

  // Priority 1: Handle RFC 7807 Problem Details in openapi-react-query format
  if (isProblemDetail(error)) {
    const problem = error.response.data;
    return {
      message: extractProblemDetailMessage(problem),
      status: problem.status,
    };
  }

  // Priority 2: Handle ProblemDetail objects directly (e.g., from fetch responses)
  if (isProblemDetailObject(error)) {
    return {
      message: extractProblemDetailMessage(error),
      status: error.status,
    };
  }

  // Priority 3: Check if error.response.data is a ProblemDetail (alternative format)
  if (
    isObject(error) &&
    isObject(error.response) &&
    isObject(error.response.data) &&
    isProblemDetailObject(error.response.data)
  ) {
    const problem = error.response.data;
    return {
      message: extractProblemDetailMessage(problem),
      status: problem.status,
    };
  }

  // Handle openapi-react-query error format
  if (isApiErrorResponse(error)) {
    return {
      message: error.response.data.error,
      status: error.response.status ?? status,
    };
  }

  // Handle fetch error format
  if (isFetchError(error)) {
    if (typeof error.error === "string") {
      return {
        message: error.error,
        status,
      };
    }
    return {
      message: error.error.message,
      status,
    };
  }

  // Handle error with message property directly
  if (isMessageError(error)) {
    return {
      message: error.message,
      status,
    };
  }

  // Handle Zod validation errors
  if (isZodError(error)) {
    const firstIssue = error.issues[0];
    return {
      message: `Validation error: ${firstIssue.message}`,
      status,
    };
  }

  // Handle string errors directly
  if (typeof error === "string") {
    return {
      message: error,
      status,
    };
  }

  // Fallback for unknown error types
  return {
    message: "An unexpected error occurred. Please try again.",
    status,
  };
};

export const showErrorToast = (error: ApiError) => {
  toast.error(error.message, {
    duration: 5000,
    position: "top-right",
    style: {
      background: "#fef2f2",
      color: "#dc2626",
      border: "1px solid #fecaca",
    },
  });
};

export const showSuccessToast = (message: string) => {
  toast.success(message, {
    duration: 4000,
    position: "top-right",
    style: {
      background: "#f0fdf4",
      color: "#16a34a",
      border: "1px solid #bbf7d0",
    },
  });
};
